/**
 * Sawyer C3 — load a groom's requested add-ons for the tier step and hold the
 * desk's ticks. Every billable request starts TICKED (the default HTHD has not
 * decided yet: shown at checkout ticked, the desk unticks what was not done).
 * A null booking id (a non-grooming visit, or no dialog) never calls the read.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRequestedAddOns } from '../../lib/requested-add-ons-api.js';
import type { RequestedAddOnsControl, RequestedAddOnsState } from './RequestedAddOns.js';

/** One constant to flip if HTHD wants requests unticked by default. */
export const REQUESTED_ADD_ONS_TICKED_BY_DEFAULT = true;

export function useRequestedAddOns(
  apiUrl: string,
  token: string | null,
  bookingId: string | null,
): RequestedAddOnsControl | null {
  // Tagged with the booking it answers for, so a step that moves to the next
  // groom never shows (or applies) the previous groom's requests for a frame.
  const [loaded, setLoaded] = useState<{ bookingId: string; state: RequestedAddOnsState } | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!bookingId || !token) return undefined;
    let live = true;
    setLoaded({ bookingId, state: { kind: 'loading' } });
    setSelected(new Set());
    void getRequestedAddOns(apiUrl, token, bookingId).then((result) => {
      if (!live) return;
      setLoaded({ bookingId, state: result });
      if (result.kind === 'ok' && REQUESTED_ADD_ONS_TICKED_BY_DEFAULT) {
        setSelected(new Set(result.requested.filter((r) => r.status === 'billable').map((r) => r.addOnId)));
      }
    });
    return () => {
      live = false;
    };
  }, [apiUrl, token, bookingId, attempt]);

  const onToggle = useCallback((addOnId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(addOnId)) next.delete(addOnId);
      else next.add(addOnId);
      return next;
    });
  }, []);
  const onRetry = useCallback(() => setAttempt((n) => n + 1), []);

  return useMemo(() => {
    if (!bookingId || !token) return null;
    const current = loaded && loaded.bookingId === bookingId ? loaded.state : ({ kind: 'loading' } as const);
    return { state: current, selected: current.kind === 'ok' ? selected : new Set<string>(), onToggle, onRetry };
  }, [bookingId, token, loaded, selected, onToggle, onRetry]);
}
